import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { API_ENDPOINTS } from '../../config/api';

const DEFAULT_PLAY_STORE =
  'https://play.google.com/store/apps/details?id=in.restroai.lite';

export default function RestroLiteFcmPushPanel() {
  const [stats, setStats] = useState({ totalDevices: 0, uniqueShops: 0, firebaseReady: false });
  const [pushTitle, setPushTitle] = useState('PocketBillingAI update available');
  const [pushBody, setPushBody] = useState(
    'A new version is on Google Play. Tap to update and get the latest features.'
  );
  const [playStoreUrl, setPlayStoreUrl] = useState(DEFAULT_PLAY_STORE);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');

  const loadStats = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(API_ENDPOINTS.RESTRO_LITE_PUSH_STATS);
      setStats(res.data || {});
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load push stats');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const sendPush = async () => {
    setBusy(true);
    setStatus('');
    setError('');
    try {
      const res = await axios.post(API_ENDPOINTS.RESTRO_LITE_PUSH_BROADCAST, {
        title: pushTitle.trim(),
        body: pushBody.trim(),
        type: 'app_update',
        playStoreUrl: playStoreUrl.trim() || DEFAULT_PLAY_STORE,
      });
      setStatus(
        `Push sent to ${res.data.successCount || 0} of ${res.data.totalDevices || 0} registered devices.`
      );
      loadStats();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send push notification');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="rounded-2xl border border-violet-200 bg-gradient-to-br from-violet-50/80 to-white p-4 sm:p-6 shadow-sm">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
        <div>
          <p className="text-xs font-semibold text-violet-600 uppercase tracking-wide">
            Firebase push (FCM)
          </p>
          <h2 className="text-lg font-bold text-stone-900 mt-0.5">
            Notify installed PocketBillingAI users
          </h2>
          <p className="text-sm text-stone-600 mt-1 max-w-2xl">
            Sends a phone notification even when the app is closed. Users must have opened the app,
            allowed notifications, and signed in at least once.
          </p>
        </div>
        <span
          className={`inline-flex shrink-0 self-start items-center rounded-full px-3 py-1 text-xs font-semibold ${
            stats.firebaseReady ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-900'
          }`}
        >
          {stats.firebaseReady ? 'PocketBillingAI Firebase ready' : 'PocketBillingAI Firebase not configured'}
        </span>
      </div>

      {loading ? (
        <p className="text-sm text-stone-500">Loading push stats…</p>
      ) : (
        <p className="text-sm text-stone-700 mb-4">
          <strong>{stats.totalDevices || 0}</strong> devices registered
          {stats.uniqueShops != null ? (
            <>
              {' '}
              from <strong>{stats.uniqueShops}</strong> shops
            </>
          ) : null}
        </p>
      )}

      <div className="grid gap-4 sm:grid-cols-2">
        <label className="block sm:col-span-2">
          <span className="text-xs font-medium text-stone-600 uppercase tracking-wide">
            Notification title
          </span>
          <input
            type="text"
            value={pushTitle}
            onChange={(e) => setPushTitle(e.target.value)}
            className="mt-1 w-full rounded-lg border border-stone-300 px-3 py-2 text-sm"
          />
        </label>
        <label className="block sm:col-span-2">
          <span className="text-xs font-medium text-stone-600 uppercase tracking-wide">
            Notification message
          </span>
          <textarea
            rows={3}
            value={pushBody}
            onChange={(e) => setPushBody(e.target.value)}
            className="mt-1 w-full rounded-lg border border-stone-300 px-3 py-2 text-sm resize-y"
          />
        </label>
        <label className="block sm:col-span-2">
          <span className="text-xs font-medium text-stone-600 uppercase tracking-wide">
            Play Store URL (opens when user taps update push)
          </span>
          <input
            type="url"
            value={playStoreUrl}
            onChange={(e) => setPlayStoreUrl(e.target.value)}
            className="mt-1 w-full rounded-lg border border-stone-300 px-3 py-2 text-sm"
          />
        </label>
      </div>

      <div className="mt-5">
        <button
          type="button"
          disabled={busy || !stats.firebaseReady}
          onClick={sendPush}
          className="px-4 py-2.5 rounded-lg bg-violet-600 text-white text-sm font-medium hover:bg-violet-700 disabled:opacity-60"
        >
          {busy ? 'Sending…' : 'Send push to all devices'}
        </button>
      </div>

      {status && (
        <p className="mt-3 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
          {status}
        </p>
      )}
      {error && (
        <p className="mt-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
          {error}
        </p>
      )}
    </section>
  );
}
